/**
 * Setting + Decor systems.
 *
 * Setting: its own RNG family picks a setting theme — a decor pack plus rule
 * table (piece-name patterns × distance bands). Data-driven: new packs are a
 * manifest + a rule table away.
 *
 * Decor: a WORLD FIELD. The caller supplies a cell region and a track-distance
 * oracle; placeDecor realizes the field for exactly that region. Every cell's
 * outcome is keyed by its own RNG child (`cell/${x}/${z}`) and the oracle's
 * answer — never by iteration order or region bounds — so any tiling of
 * regions produces the same world. Single-track mode passes bounds+margin and
 * an oracle over its own cell set; M5's chunk streamer passes chunk regions
 * and an oracle backed by "this chunk + neighbors".
 */
import type { Placement, Vec3 } from "../shared/types.ts";
import type { Rng } from "./rng.ts";
import type { StyleField } from "./style.ts";

/** What the generator needs to know about available decor pieces. */
export interface DecorCatalog {
  packs: { slug: string; ids: readonly string[] }[];
}

type Band = "near" | "mid" | "far" | "ambient";

interface DecorGroup {
  pattern: RegExp;
  weight: number;
  band: Band;
  scale: readonly [number, number];
}

export interface Setting {
  name: string;
  packSlug: string | null;
  groups: DecorGroup[];
}

/** Setting rule rows. `pack` names the decor pack the patterns are scoped
 * to (per-pack scoping makes cross-pack regex collisions impossible);
 * `finite` marks the rows the LOOP mode's setting draw picks from — that
 * list must stay exactly the historical three, in order, or every finite
 * seed's `setting` stream reshuffles (genVersion moves). Endless biomes
 * reach any row by name via settingByName. */
type SettingSpec = Omit<Setting, "packSlug"> & {
  pack: string;
  finite?: boolean;
};

const SETTINGS: SettingSpec[] = [
  {
    name: "forest",
    pack: "nature-kit",
    finite: true,
    groups: [
      {
        pattern: /^tree[-_]?(?!.*dead)/,
        weight: 6,
        band: "mid",
        scale: [0.8, 1.3],
      },
      { pattern: /^tree/, weight: 3, band: "far", scale: [1.0, 1.6] },
      { pattern: /^(rock|stone)/, weight: 2, band: "near", scale: [0.6, 1.1] },
      {
        pattern: /^(grass|plant|flower|mushroom)/,
        weight: 4,
        band: "near",
        scale: [0.8, 1.2],
      },
      { pattern: /^stump/, weight: 1, band: "mid", scale: [0.8, 1.2] },
    ],
  },
  {
    name: "rocky",
    pack: "nature-kit",
    finite: true,
    groups: [
      {
        pattern: /^(rock|stone|cliff)/,
        weight: 6,
        band: "mid",
        scale: [0.8, 1.5],
      },
      { pattern: /^(rock|stone)/, weight: 4, band: "near", scale: [0.5, 0.9] },
      {
        pattern: /^tree.*dead|^log/,
        weight: 2,
        band: "mid",
        scale: [0.9, 1.2],
      },
      { pattern: /^(grass|plant)/, weight: 2, band: "near", scale: [0.7, 1.0] },
      {
        pattern: /^cliff|^rock[-_]?large/,
        weight: 3,
        band: "far",
        scale: [1.2, 2.0],
      },
    ],
  },
  {
    name: "meadow",
    pack: "nature-kit",
    finite: true,
    groups: [
      {
        pattern: /^(grass|flower|plant)/,
        weight: 6,
        band: "near",
        scale: [0.8, 1.3],
      },
      { pattern: /^(fence|path)/, weight: 1.5, band: "near", scale: [1, 1] },
      { pattern: /^tree/, weight: 2, band: "far", scale: [1.0, 1.5] },
      {
        pattern: /^(crop|pumpkin|bed)/,
        weight: 2,
        band: "mid",
        scale: [0.9, 1.1],
      },
    ],
  },
  // M18a themed rows (endless biomes only — never in the finite draw).
  // Scale ranges are measured against each pack's authored size (pirate
  // pieces are ~2.5-3x nature scale; survival props run tiny).
  {
    name: "winter",
    pack: "holiday-kit",
    groups: [
      { pattern: /^tree-snow/, weight: 6, band: "mid", scale: [0.7, 1.0] },
      { pattern: /^tree-snow/, weight: 3, band: "far", scale: [0.9, 1.2] },
      { pattern: /^rocks-(small|medium)/, weight: 2, band: "near", scale: [0.5, 0.8] },
      { pattern: /^rocks-large/, weight: 2, band: "far", scale: [0.4, 0.6] },
      { pattern: /^snow-pile/, weight: 3, band: "near", scale: [0.8, 1.2] },
      { pattern: /^(present-|candy-cane)/, weight: 1.5, band: "near", scale: [0.8, 1.2] },
      { pattern: /^lantern$/, weight: 1, band: "near", scale: [0.5, 0.7] },
      { pattern: /^tree-decorated$/, weight: 0.8, band: "mid", scale: [0.8, 0.95] },
    ],
  },
  {
    name: "spooky",
    pack: "graveyard-kit",
    groups: [
      { pattern: /^pine/, weight: 5, band: "mid", scale: [0.6, 0.9] },
      { pattern: /^pine/, weight: 3, band: "far", scale: [0.8, 1.1] },
      { pattern: /^gravestone/, weight: 5, band: "near", scale: [0.9, 1.3] },
      { pattern: /^iron-fence$/, weight: 2, band: "near", scale: [0.9, 1.1] },
      { pattern: /^pumpkin/, weight: 2, band: "near", scale: [0.9, 1.3] },
      { pattern: /^(lantern-candle|candle)/, weight: 1.5, band: "near", scale: [1.0, 1.3] },
      { pattern: /^(debris|rocks|trunk)/, weight: 2, band: "mid", scale: [0.7, 1.1] },
      { pattern: /^cross(-wood)?$/, weight: 1.5, band: "mid", scale: [1.0, 1.3] },
    ],
  },
  {
    name: "tropical",
    pack: "pirate-kit",
    groups: [
      { pattern: /^palm-(bend|straight)/, weight: 6, band: "mid", scale: [0.3, 0.42] },
      { pattern: /^palm-detailed/, weight: 3, band: "far", scale: [0.35, 0.5] },
      { pattern: /^rocks-sand/, weight: 3, band: "near", scale: [0.15, 0.3] },
      { pattern: /^grass$/, weight: 3, band: "near", scale: [0.8, 1.2] },
      { pattern: /^grass-plant$/, weight: 2, band: "near", scale: [0.5, 0.8] },
      { pattern: /^(barrel|crate|chest)$/, weight: 1.5, band: "near", scale: [0.5, 0.7] },
      { pattern: /^flag$/, weight: 0.8, band: "mid", scale: [0.5, 0.7] },
    ],
  },
  {
    name: "autumn",
    pack: "survival-kit",
    groups: [
      { pattern: /^tree-autumn/, weight: 6, band: "mid", scale: [0.9, 1.3] },
      { pattern: /^tree-autumn-tall/, weight: 3, band: "far", scale: [1.1, 1.5] },
      { pattern: /^rock-(a|b|c|flat)/, weight: 2.5, band: "near", scale: [0.8, 1.2] },
      { pattern: /^rock-sand/, weight: 2, band: "mid", scale: [0.7, 1.1] },
      { pattern: /^grass/, weight: 3, band: "near", scale: [1.0, 1.4] },
      { pattern: /^(tent|campfire)/, weight: 1.5, band: "mid", scale: [1.3, 1.7] },
      { pattern: /^signpost/, weight: 0.8, band: "near", scale: [1.2, 1.5] },
      { pattern: /^tree-log/, weight: 1.5, band: "near", scale: [1.0, 1.4] },
    ],
  },
];

/** Resolve one settings row against its own pack in the catalog: groups
 * whose pattern matches nothing in the pack are dropped (pack versions
 * drift; the field must never place a missing id). */
function resolveSetting(
  spec: SettingSpec,
  catalog: DecorCatalog,
): Setting {
  const pack = catalog.packs.find((p) => p.slug === spec.pack);
  if (!pack) return { name: "bare", packSlug: null, groups: [] };
  return {
    name: spec.name,
    packSlug: spec.pack,
    groups: spec.groups.filter((g) => pack.ids.some((id) => g.pattern.test(id))),
  };
}

/**
 * Setting by name — island biomes (endless mode) reach any rule row
 * directly (nature rows and the M18a themed rows alike) instead of
 * drawing a setting per world.
 */
export function settingByName(name: string, catalog: DecorCatalog): Setting {
  const spec = SETTINGS.find((s) => s.name === name);
  if (!spec) return { name: "bare", packSlug: null, groups: [] };
  return resolveSetting(spec, catalog);
}

export function pickSetting(rng: Rng, catalog: DecorCatalog): Setting {
  // LOOP mode's per-world draw: exactly the historical rows, in order —
  // the `finite` flag is the genVersion guard (see SETTINGS).
  const spec = rng.pick(SETTINGS.filter((s) => s.finite));
  return resolveSetting(spec, catalog);
}

/** Marble Kit decor sprinkled independently of the setting pack. */
const MARBLE_DECOR: {
  id: string;
  weight: number;
  band: Band;
  spin?: boolean;
}[] = [
  { id: "tree", weight: 2, band: "mid" },
  { id: "tree-tall", weight: 2, band: "mid" },
  { id: "tree-large", weight: 1, band: "far" },
  { id: "tree-tall-large", weight: 1, band: "far" },
  // Banners are WIDE (posts at ±0.425, plus placement jitter ±0.3): in the
  // near band they can reach across into an adjacent track cell (caught by
  // validate at 1/1000 seeds). Mid band keeps them clear.
  { id: "banner", weight: 1, band: "mid" },
  { id: "banner-high", weight: 0.7, band: "mid" },
  { id: "banner-double", weight: 0.5, band: "mid" },
  { id: "banner-double-high", weight: 0.4, band: "mid" },
  { id: "banner-hanging", weight: 0.5, band: "mid" },
  { id: "banner-hanging-high", weight: 0.4, band: "mid" },
  { id: "banner-hanging-double", weight: 0.3, band: "mid" },
  { id: "banner-hanging-double-high", weight: 0.3, band: "mid" },
  // Fans are TRACK ELEMENTS now (fans.ts, M4): deliberately placed against
  // the channel with a marble boost — no longer sprinkled as decor.
];

/** Inclusive cell bounds the decor field is realized for. */
export interface CellRegion {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

/**
 * What the field knows about the track at a cell: distance to the nearest
 * track cell and the grid direction toward it (as a piece rotation, so facade
 * pieces like fans can face the track). `dist` is true out to
 * ORACLE_GRADE_HORIZON (the graded ambient falloff needs it) and Infinity
 * beyond; everything past 5.5 is the ambient band.
 */
export interface TrackProbe {
  dist: number;
  dir: Placement["rotation"];
}

/**
 * Cell → nearest-track info. A chunked caller (M5) backs this with
 * "this chunk + neighbors"; single-track mode wraps its one cell set.
 * Implementations must answer from bounded local context only.
 */
export type TrackOracle = (x: number, z: number) => TrackProbe;

/**
 * How far (in cells) an oracle must see: the banded rules stop at 5.5, so any
 * track farther than this cannot change a cell's BAND. Chunked backers
 * need neighbor context of exactly ORACLE_GRADE_HORIZON.
 */
export const ORACLE_HORIZON = 6;

/**
 * Widened oracle contract (M13, the promised distance-graded ambient):
 * oracles return TRUE distances out to this radius (matching the
 * single-track margin) and saturate to Infinity beyond — so the ambient
 * band can thin with distance instead of a flat factor. This widens the
 * CONTRACT, not the field rules: the graded density is the only consumer.
 */
export const ORACLE_GRADE_HORIZON = 13;

/** Track bounds + margin — the single-track region. */
export function regionAround(
  cells: ReadonlySet<string>,
  margin: number,
): CellRegion {
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  for (const cell of cells) {
    const [x, z] = cell.split(",").map(Number);
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minZ = Math.min(minZ, z);
    maxZ = Math.max(maxZ, z);
  }
  return {
    minX: minX - margin,
    maxX: maxX + margin,
    minZ: minZ - margin,
    maxZ: maxZ + margin,
  };
}

/** Oracle over a plain cell set (single-track mode). */
export function cellSetOracle(cells: ReadonlySet<string>): TrackOracle {
  return (x, z) => {
    let best = Infinity;
    let bx = 0, bz = 1;
    for (let dx = -ORACLE_GRADE_HORIZON; dx <= ORACLE_GRADE_HORIZON; dx++) {
      for (let dz = -ORACLE_GRADE_HORIZON; dz <= ORACLE_GRADE_HORIZON; dz++) {
        if (cells.has(`${x + dx},${z + dz}`)) {
          const d = Math.hypot(dx, dz);
          if (d < best) {
            best = d;
            bx = dx;
            bz = dz;
          }
        }
      }
    }
    // Dominant axis → Dir (0=+x, 1=+z, 2=−x, 3=−z).
    const wantDir = Math.abs(bx) >= Math.abs(bz)
      ? (bx >= 0 ? 0 : 2)
      : (bz >= 0 ? 1 : 3);
    // Rotation r maps local +z (dir 1) to world (1 − r + 4) % 4.
    return {
      dist: best,
      dir: ((1 - wantDir + 4) % 4) as Placement["rotation"],
    };
  };
}

/** Ambient band thins the field out toward the fog, not a hard cliff. */
const AMBIENT_DENSITY = 0.3;
/** Graded falloff floor at/beyond the grade horizon — the last stragglers
 * the fog eats. */
const AMBIENT_FLOOR = 0.05;

/** Distance-graded ambient density (M13): 0.3 at the far-band edge
 * thinning linearly to the floor at ORACLE_GRADE_HORIZON. Saturated
 * distances (Infinity) sit at the floor. */
export function ambientDensity(dist: number): number {
  const span = ORACLE_GRADE_HORIZON - 5.5;
  const t = Math.max(
    0,
    Math.min(1, (Math.min(dist, ORACLE_GRADE_HORIZON) - 5.5) / span),
  );
  return AMBIENT_DENSITY + (AMBIENT_FLOOR - AMBIENT_DENSITY) * t;
}

export function placeDecor(
  rng: Rng,
  setting: Setting,
  style: StyleField,
  region: CellRegion,
  oracle: TrackOracle,
  catalog: DecorCatalog,
): Placement[] {
  const placements: Placement[] = [];
  const nature = setting.packSlug
    ? catalog.packs.find((p) => p.slug === setting.packSlug)
    : undefined;

  const bandOf = (d: number): Band | null => {
    if (d < 1) return null; // on/too close to the track — would clip it
    if (d <= 2) return "near";
    if (d <= 3.5) return "mid";
    if (d <= 5.5) return "far";
    return "ambient"; // sparse continuation out to the region edge
  };

  const idsFor = (group: DecorGroup): string[] =>
    nature ? nature.ids.filter((id) => group.pattern.test(id)) : [];

  for (let x = region.minX; x <= region.maxX; x++) {
    for (let z = region.minZ; z <= region.maxZ; z++) {
      const cellRng = rng.child(`cell/${x}/${z}`);
      if (!cellRng.chance(style.decorDensity * 0.45)) continue;
      const probe = oracle(x, z);
      const band = bandOf(probe.dist);
      if (band === null) continue;
      // The ambient band reuses the far-band rules, thinned: horizon fill of
      // the same trees/rocks/cliffs, sparse enough that the fog does the rest.
      const ruleBand: Band = band === "ambient" ? "far" : band;
      if (band === "ambient" && !cellRng.chance(ambientDensity(probe.dist))) {
        continue;
      }

      // Weighted union of setting groups and marble-kit decor for this band.
      const options: (readonly [() => Placement | null, number])[] = [];
      for (const group of setting.groups) {
        if (group.band !== ruleBand) continue;
        const ids = idsFor(group);
        if (ids.length === 0) continue;
        options.push(
          [
            () =>
              makeNaturePlacement(cellRng, setting.packSlug!, ids, group, x, z),
            group.weight,
          ] as const,
        );
      }
      for (const d of MARBLE_DECOR) {
        if (d.band !== ruleBand) continue;
        // Fans stay banded: a fan spinning alone in the ambient void reads
        // as debris, not scenery.
        if (band === "ambient" && d.spin) continue;
        options.push(
          [
            () => makeMarblePlacement(cellRng, d, x, z, probe.dir),
            d.weight * 0.5,
          ] as const,
        );
      }
      if (options.length === 0) continue;
      const make = cellRng.weighted(options);
      const placement = make();
      if (placement) placements.push(placement);
    }
  }
  return placements;
}

function jitteredPos(rng: Rng, x: number, z: number): Vec3 {
  return [x + (rng.float() - 0.5) * 0.6, 0, z + (rng.float() - 0.5) * 0.6];
}

function makeNaturePlacement(
  rng: Rng,
  packSlug: string,
  ids: string[],
  group: DecorGroup,
  x: number,
  z: number,
): Placement {
  const id = rng.pick(ids);
  const scale = group.scale[0] +
    rng.float() * (group.scale[1] - group.scale[0]);
  return {
    pieceId: `${packSlug}:${id}`,
    pos: jitteredPos(rng, x, z),
    rotation: rng.int(0, 4) as Placement["rotation"],
    role: "decor",
    scale,
  };
}

function makeMarblePlacement(
  rng: Rng,
  d: { id: string; spin?: boolean },
  x: number,
  z: number,
  faceTrack: Placement["rotation"],
): Placement {
  const pos = jitteredPos(rng, x, z);
  // Spinning pieces (fans) face the track — their local +z is the blade
  // side, and the housing back reads as a blank deck-colored cube.
  const rotation = d.spin ? faceTrack : rng.int(0, 4) as Placement["rotation"];
  return {
    pieceId: d.id,
    pos,
    rotation,
    role: "decor",
    spin: d.spin
      ? { axis: [0, 0, 1], radPerSec: 1.5 + rng.float() * 2.5 }
      : undefined,
  };
}
